import React, { FC, useMemo } from "react";
import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import styled from "styled-components";
import { FoodTagBasic, FoodTagCategory } from "client/jspPlatformExperiment";
import { FoodEffectiveTagsState } from "hooks/food/useFoodEffectiveTags";
import {
  getFoodTagCategoryDisplayName,
  sortFoodTagsByCategoryRank,
} from "utils/food/foodTagDisplayUtils";

const Title = styled(Typography)`
  font-size: 0.8rem;
  font-weight: 500;
  color: #a0aab4;
`;

/** A tag the override hides: kept in its group, but visibly struck out. */
const HiddenChip = styled(Chip)`
  color: #a0aab4;
  border-color: #cfd6dd;
  border-style: dashed;
  text-decoration: line-through;
`;

const UNKNOWN_CATEGORY = "[Unknown category]";

/**
 * Group heading for a tag. The shared helper only names the four editable
 * categories; effective tags also carry allergens and food categories.
 */
const categoryHeading = (tag: FoodTagBasic): string => {
  const name = getFoodTagCategoryDisplayName(tag);
  if (name !== UNKNOWN_CATEGORY) {
    return name;
  }
  if (tag.tag_categories.includes(FoodTagCategory.ALLERGEN)) {
    return "Allergen";
  }
  if (tag.tag_categories.includes(FoodTagCategory.FOOD_CATEGORY)) {
    return "Food Category";
  }
  return "Other";
};

/** Tags grouped by heading, in the fixed category order. */
const groupByCategory = (tags: FoodTagBasic[]): [string, FoodTagBasic[]][] => {
  const groups = new Map<string, FoodTagBasic[]>();
  sortFoodTagsByCategoryRank(tags).forEach((tag) => {
    const key = categoryHeading(tag);
    groups.set(key, [...(groups.get(key) ?? []), tag]);
  });
  return [...groups.entries()];
};

const tagLabel = (tag: FoodTagBasic): string =>
  tag.tag_name ?? tag.tag_description ?? `Tag ${tag.id}`;

const NO_TAGS: FoodTagBasic[] = [];

export interface EffectiveTagsPreviewProps {
  preview: FoodEffectiveTagsState;
}

/**
 * Contents of the preview popover: the tags a menu item reports with its
 * overrides applied, with the food's own tags the override hides shown in
 * place, struck out, inside their category group.
 */
const EffectiveTagsPreview: FC<EffectiveTagsPreviewProps> = ({ preview }) => {
  const effective = preview.data?.food_tags ?? NO_TAGS;
  const removable = preview.data?.removable_tags ?? NO_TAGS;
  const { allTags, hiddenIds } = useMemo(() => {
    const effectiveIds = new Set(effective.map((t) => t.id));
    const hiddenTags = removable.filter((t) => !effectiveIds.has(t.id));
    return {
      allTags: [...effective, ...hiddenTags],
      hiddenIds: new Set(hiddenTags.map((t) => t.id)),
    };
  }, [effective, removable]);

  return (
    <Box padding={2} width="24rem" maxWidth="90vw">
      <Title>Preview</Title>
      {preview.isLoading ? <Skeleton height={60} /> : null}
      {preview.error ? (
        <Alert severity="error" role="alert" sx={{ marginTop: 1 }}>
          {preview.error}
        </Alert>
      ) : null}
      {preview.data ? (
        <>
          {groupByCategory(allTags).map(([category, tags]) => (
            <Box key={category} marginTop={1}>
              <Typography variant="caption">{category}</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {tags.map((tag) =>
                  hiddenIds.has(tag.id) ? (
                    <HiddenChip
                      key={tag.id}
                      size="small"
                      variant="outlined"
                      label={tagLabel(tag)}
                      aria-label={`${tagLabel(tag)} (hidden by the override)`}
                    />
                  ) : (
                    <Chip key={tag.id} size="small" label={tagLabel(tag)} />
                  ),
                )}
              </Stack>
            </Box>
          ))}
        </>
      ) : null}
    </Box>
  );
};

export default EffectiveTagsPreview;
